// pages/XMLConfiguracion.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Card,
  Tabs,
  Select
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SaveOutlined,
  CloseOutlined,
  ApartmentOutlined,
  HomeOutlined
} from '@ant-design/icons';
import axios from 'axios';
import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { TabPane } = Tabs;
const { Option } = Select;

const XMLConfiguracion = () => {
  const [regimenes, setRegimenes] = useState([]);
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('regimen');
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();
  const [hotelFilter, setHotelFilter] = useState('1');
  const [agenciaFilter, setAgenciaFilter] = useState('Directo');

  useEffect(() => {
    cargarRegimenes();
    cargarHabitaciones();
  }, [hotelFilter, agenciaFilter]);

  const cargarRegimenes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/xml/regimenes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hotel: hotelFilter, agencia: agenciaFilter }
      });
      setRegimenes(response.data.data || []);
    } catch (error) {
      message.error('Error cargando regímenes');
    } finally {
      setLoading(false);
    }
  };

  const cargarHabitaciones = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/xml/habitaciones`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { hotel: hotelFilter, nombre: agenciaFilter }
      });
      setHabitaciones(response.data.data || []);
    } catch (error) {
      message.error('Error cargando habitaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record, type) => {
    setModalType(type);
    setCurrentRecord(record);
    form.setFieldsValue({
      RegimenAnphitrion: record.RegimenAnphitrion,
      TipoHab: record.TipoHab,
      HabitacionVenta: record.HabitacionVenta,
      HabitacionEspecial: record.HabitacionEspecial
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      if (modalType === 'regimen') {
        await axios.put(
          `${BASE_URL}/api/xml/regimenes/${currentRecord.id}`,
          { RegimenAnphitrion: values.RegimenAnphitrion },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Régimen actualizado correctamente');
        cargarRegimenes();
      } else {
        await axios.put(
          `${BASE_URL}/api/xml/habitaciones/${currentRecord.id}`,
          {
            TipoHab: values.TipoHab,
            HabitacionVenta: values.HabitacionVenta,
            HabitacionEspecial: values.HabitacionEspecial
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Habitación actualizada correctamente');
        cargarHabitaciones();
      }
      
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('Error al guardar');
    }
  };

  const handleDelete = async (record, type) => {
    try {
      const token = localStorage.getItem('token');
      
      if (type === 'regimen') {
        await axios.delete(`${BASE_URL}/api/xml/regimenes/${record.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Régimen eliminado');
        cargarRegimenes();
      } else {
        await axios.delete(`${BASE_URL}/api/xml/habitaciones/${record.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Habitación eliminada');
        cargarHabitaciones();
      }
    } catch (error) {
      message.error('Error al eliminar');
    }
  };

  const columnsRegimenes = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Régimen Original',
      dataIndex: 'Regimen',
      key: 'Regimen',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Agencia XML',
      dataIndex: 'AgenciaXML',
      key: 'AgenciaXML'
    },
    {
      title: 'Hotel',
      dataIndex: 'Hotel',
      key: 'Hotel'
    },
    {
      title: 'Régimen Anphitrion',
      dataIndex: 'RegimenAnphitrion',
      key: 'RegimenAnphitrion',
      render: (text) => <Tag color="green">{text}</Tag>
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              icon={<EditOutlined />}
              size="small"
              type="primary"
              onClick={() => handleEdit(record, 'regimen')}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(record, 'regimen')}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const columnsHabitaciones = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Código Habitación',
      dataIndex: 'CodigoHabitacion',
      key: 'CodigoHabitacion',
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Hotel',
      dataIndex: 'HotelID',
      key: 'HotelID'
    },
    {
      title: 'Nombre',
      dataIndex: 'Nombre',
      key: 'Nombre'
    },
    {
      title: 'Tipo Hab',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      render: (text) => <Tag color="cyan">{text}</Tag>
    },
    {
      title: 'Habitación Venta',
      dataIndex: 'HabitacionVenta',
      key: 'HabitacionVenta'
    },
    {
      title: 'Habitación Especial',
      dataIndex: 'HabitacionEspecial',
      key: 'HabitacionEspecial',
      render: (text) => text || '-'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              icon={<EditOutlined />}
              size="small"
              type="primary"
              onClick={() => handleEdit(record, 'habitacion')}
            />
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              icon={<DeleteOutlined />}
              size="small"
              danger
              onClick={() => handleDelete(record, 'habitacion')}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <PageContainer
      title="Configuración XML"
      subtitle="Gestiona los mapeos de regímenes y habitaciones"
      icon={<ApartmentOutlined />}
    >
      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              addonBefore="Hotel"
              value={hotelFilter}
              onChange={(e) => setHotelFilter(e.target.value)}
              placeholder="ID Hotel"
            />
          </Col>
          <Col span={8}>
            <Input
              addonBefore="Agencia"
              value={agenciaFilter}
              onChange={(e) => setAgenciaFilter(e.target.value)}
              placeholder="Agencia XML"
            />
          </Col>
          <Col span={8}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                cargarRegimenes();
                cargarHabitaciones();
              }}
            >
              Recargar
            </Button>
          </Col>
        </Row>

        <Tabs defaultActiveKey="1">
          <TabPane 
            tab={<span><ApartmentOutlined /> Regímenes</span>} 
            key="1"
          >
            <Table
              columns={columnsRegimenes}
              dataSource={regimenes}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1000 }}
            />
          </TabPane>
          
          <TabPane 
            tab={<span><HomeOutlined /> Habitaciones</span>} 
            key="2"
          >
            <Table
              columns={columnsHabitaciones}
              dataSource={habitaciones}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Modal de edición */}
      <Modal
        title={modalType === 'regimen' ? 'Editar Régimen' : 'Editar Habitación'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">
          {modalType === 'regimen' ? (
            <Form.Item
              name="RegimenAnphitrion"
              label="Régimen Anphitrion"
              rules={[{ required: true, message: 'Campo requerido' }]}
            >
              <Select placeholder="Seleccionar régimen">
                <Option value="PC">PC - Pensión Completa</Option>
                <Option value="MP">MP - Media Pensión</Option>
                <Option value="AD">AD - Alojamiento y Desayuno</Option>
                <Option value="AL">AL - Alojamiento</Option>
                <Option value="TI">TI - Todo Incluido</Option>
                <Option value="HD">HD - Half Board</Option>
              </Select>
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="TipoHab"
                label="Tipo Habitación"
                rules={[{ required: true, message: 'Campo requerido' }]}
              >
                <Select placeholder="Seleccionar tipo">
                  <Option value="IN">IN - Individual</Option>
                  <Option value="DO">DO - Doble</Option>
                  <Option value="TR">TR - Triple</Option>
                  <Option value="CU">CU - Cuádruple</Option>
                  <Option value="QU">QU - Quíntuple</Option>
                  <Option value="DUI">DUI - Doble uso individual</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="HabitacionVenta"
                label="Habitación Venta"
              >
                <Input placeholder="Ej: DO, TR, etc." />
              </Form.Item>

              <Form.Item
                name="HabitacionEspecial"
                label="Habitación Especial"
              >
                <Input placeholder="Ej: VISTA MAR, SUITE, etc." />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default XMLConfiguracion;